"use client";

import {
  Heart,
  Clock,
  Send,
  AlertCircle,
  Smartphone,
  MessageSquare,
  Check,
  X,
  Calendar,
  User,
  Loader2,
  Gift,
  PartyPopper,
  Cake,
  Settings,
  ChevronRight,
  RotateCcw,
  Shield,
  AlertTriangle,
  Award,
  UtensilsCrossed,
  Bell,
  FileWarning,
  Mic,
  Sparkles,
  Lock,
} from "lucide-react";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { toZonedTime, format as formatTz } from "date-fns-tz";
import { useRouter, useSearchParams } from "next/navigation";

import { Button } from "@/app/_module/components/ui/button";
import { A2PGateNotice } from "@/app/_module/components/A2PGateNotice";
import { useToast } from "@/app/_module/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import { Label } from "@/app/_module/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Badge } from "@/app/_module/components/ui/badge";
import { Input } from "@/app/_module/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/app/_module/components/ui/radio-group";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import {
  useSentimentalSettings,
  useUpdateSentimentalSettings,
  useTestSend,
  useSentimentalSchedules,
  useApproveSchedule,
  useRejectSchedule,
  useApproveAll,
  useUpcomingOccasions,
} from "@/app/_module/hooks/useSentimentals";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { a2pService } from "@/app/_module/services/a2pService";
import type { SentimentalSettings, OccasionType, AutomationMode } from "@/app/_module/services/sentimentalsService";
import { useVoiceClones } from "@/app/_module/hooks/useVoiceClone";

// Common US timezones
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona Time (MST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
];

// US State to timezone mapping
const STATE_TIMEZONES: Record<string, { timezone: string; abbrev: string }> = {
  // Eastern Time
  CT: { timezone: "America/New_York", abbrev: "ET" },
  DE: { timezone: "America/New_York", abbrev: "ET" },
  DC: { timezone: "America/New_York", abbrev: "ET" },
  FL: { timezone: "America/New_York", abbrev: "ET" },
  GA: { timezone: "America/New_York", abbrev: "ET" },
  IN: { timezone: "America/Indiana/Indianapolis", abbrev: "ET" },
  KY: { timezone: "America/New_York", abbrev: "ET" },
  ME: { timezone: "America/New_York", abbrev: "ET" },
  MD: { timezone: "America/New_York", abbrev: "ET" },
  MA: { timezone: "America/New_York", abbrev: "ET" },
  MI: { timezone: "America/New_York", abbrev: "ET" },
  NH: { timezone: "America/New_York", abbrev: "ET" },
  NJ: { timezone: "America/New_York", abbrev: "ET" },
  NY: { timezone: "America/New_York", abbrev: "ET" },
  NC: { timezone: "America/New_York", abbrev: "ET" },
  OH: { timezone: "America/New_York", abbrev: "ET" },
  PA: { timezone: "America/New_York", abbrev: "ET" },
  RI: { timezone: "America/New_York", abbrev: "ET" },
  SC: { timezone: "America/New_York", abbrev: "ET" },
  VT: { timezone: "America/New_York", abbrev: "ET" },
  VA: { timezone: "America/New_York", abbrev: "ET" },
  WV: { timezone: "America/New_York", abbrev: "ET" },
  // Central Time
  AL: { timezone: "America/Chicago", abbrev: "CT" },
  AR: { timezone: "America/Chicago", abbrev: "CT" },
  IL: { timezone: "America/Chicago", abbrev: "CT" },
  IA: { timezone: "America/Chicago", abbrev: "CT" },
  KS: { timezone: "America/Chicago", abbrev: "CT" },
  LA: { timezone: "America/Chicago", abbrev: "CT" },
  MN: { timezone: "America/Chicago", abbrev: "CT" },
  MS: { timezone: "America/Chicago", abbrev: "CT" },
  MO: { timezone: "America/Chicago", abbrev: "CT" },
  NE: { timezone: "America/Chicago", abbrev: "CT" },
  ND: { timezone: "America/Chicago", abbrev: "CT" },
  OK: { timezone: "America/Chicago", abbrev: "CT" },
  SD: { timezone: "America/Chicago", abbrev: "CT" },
  TN: { timezone: "America/Chicago", abbrev: "CT" },
  TX: { timezone: "America/Chicago", abbrev: "CT" },
  WI: { timezone: "America/Chicago", abbrev: "CT" },
  // Mountain Time
  AZ: { timezone: "America/Phoenix", abbrev: "MT" },
  CO: { timezone: "America/Denver", abbrev: "MT" },
  ID: { timezone: "America/Boise", abbrev: "MT" },
  MT: { timezone: "America/Denver", abbrev: "MT" },
  NM: { timezone: "America/Denver", abbrev: "MT" },
  UT: { timezone: "America/Denver", abbrev: "MT" },
  WY: { timezone: "America/Denver", abbrev: "MT" },
  NV: { timezone: "America/Los_Angeles", abbrev: "PT" },
  // Pacific Time
  CA: { timezone: "America/Los_Angeles", abbrev: "PT" },
  OR: { timezone: "America/Los_Angeles", abbrev: "PT" },
  WA: { timezone: "America/Los_Angeles", abbrev: "PT" },
  // Alaska & Hawaii
  AK: { timezone: "America/Anchorage", abbrev: "AKT" },
  HI: { timezone: "Pacific/Honolulu", abbrev: "HST" },
  // Territories
  PR: { timezone: "America/Puerto_Rico", abbrev: "AST" },
  VI: { timezone: "America/Virgin", abbrev: "AST" },
  GU: { timezone: "Pacific/Guam", abbrev: "ChST" },
};

// Get timezone info for a US state
const getTimezoneForState = (state: string): { timezone: string; abbrev: string } | null => {
  if (!state) return null;
  const upperState = state.toUpperCase().trim();
  return STATE_TIMEZONES[upperState] || null;
};

// Convert scheduled time to client's local time based on their state
const getClientLocalTime = (
  scheduledDate: string,
  scheduledTime: string,
  clientState: string,
): { localTime: string; abbrev: string } | null => {
  if (!clientState || !scheduledDate || !scheduledTime) return null;

  const tzInfo = getTimezoneForState(clientState);
  if (!tzInfo) return null;

  try {
    // Create a date string and parse it
    const dateTimeStr = `${scheduledDate}T${scheduledTime}:00`;
    const serverDate = new Date(dateTimeStr);

    // Convert to client's timezone
    const clientDate = toZonedTime(serverDate, tzInfo.timezone);
    const localTime = formatTz(clientDate, "h:mm a", { timeZone: tzInfo.timezone });

    return { localTime, abbrev: tzInfo.abbrev };
  } catch (error) {
    console.error("Error converting timezone:", error);
    return null;
  }
};

// Occasion configuration
const OCCASIONS = [
  {
    id: 'birthday' as OccasionType,
    label: 'Birthday',
    icon: Cake,
    description: 'Send personalized birthday wishes',
    enabledKey: 'birthdayEnabled' as const,
    templateKey: 'birthdayTemplateId' as const,
    messageKey: 'birthdayMessage' as const,
    defaultMessage: 'Happy Birthday {{first_name}}! Hope you have an amazing day. This is a great time to review your life insurance coverage - when would be a good time for a quick call? I may be able to get you a better rate. Text or call me at {{phone}}',
  },
  {
    id: 'policy_anniversary' as OccasionType,
    label: 'Policy Anniversary',
    icon: Award,
    description: 'Celebrate 1 year since first policy',
    enabledKey: 'policyAnniversaryEnabled' as const,
    templateKey: 'policyAnniversaryTemplateId' as const,
    messageKey: 'policyAnniversaryMessage' as const,
    defaultMessage: 'Hi {{first_name}}! It\'s been a year since we got your life insurance in place. Thank you for your trust! Rates change often - when is a good time to chat and see if we can get you better coverage at a lower rate? Reply or call {{phone}}',
  },
  {
    id: 'christmas' as OccasionType,
    label: 'Christmas',
    icon: Gift,
    description: 'Send holiday greetings on Christmas',
    enabledKey: 'christmasEnabled' as const,
    templateKey: 'christmasTemplateId' as const,
    messageKey: 'christmasMessage' as const,
    defaultMessage: 'Happy Holidays {{first_name}}! Wishing you and your family a wonderful holiday season. As we head into the new year, it\'s a great time to review your coverage. Let me know if you\'d like to schedule a quick call - {{phone}}',
  },
  {
    id: 'thanksgiving' as OccasionType,
    label: 'Thanksgiving',
    icon: UtensilsCrossed,
    description: 'Send gratitude messages on Thanksgiving',
    enabledKey: 'thanksgivingEnabled' as const,
    templateKey: 'thanksgivingTemplateId' as const,
    messageKey: 'thanksgivingMessage' as const,
    defaultMessage: 'Happy Thanksgiving {{first_name}}! I\'m grateful to have you as a client. Hope you enjoy time with family today. If you ever need to review your life insurance or have questions, I\'m here for you - {{phone}}',
  },
  {
    id: 'new_year' as OccasionType,
    label: 'New Year',
    icon: PartyPopper,
    description: 'Send New Year wishes',
    enabledKey: 'newYearEnabled' as const,
    templateKey: 'newYearTemplateId' as const,
    messageKey: 'newYearMessage' as const,
    defaultMessage: 'Happy New Year {{first_name}}! New year is a perfect time to make sure your family is protected. Would love to do a quick review of your coverage - when works best for a call? Text me back or call {{phone}}',
  },
  {
    id: 'lapsed_policy' as OccasionType,
    label: 'Lapsed Policies',
    icon: FileWarning,
    description: 'Re-engage leads with expired or lapsed policies',
    enabledKey: 'lapsedPolicyEnabled' as const,
    templateKey: 'lapsedPolicyTemplateId' as const,
    messageKey: 'lapsedPolicyMessage' as const,
    defaultMessage: 'Hi {{first_name}}, I noticed your life insurance policy may have lapsed. Gaps in coverage can leave your family unprotected. I\'d love to help you get covered again quickly - rates may have even improved. Text me back or call {{phone}}',
  },
];

// Available variables for message templates (same as SMS Templates)
const AVAILABLE_VARIABLES = [
  // Basic Info
  { value: '{{name}}', label: 'Full Name', category: 'Basic Info' },
  { value: '{{first_name}}', label: 'First Name', category: 'Basic Info' },
  { value: '{{last_name}}', label: 'Last Name', category: 'Basic Info' },
  // Contact Info
  { value: '{{email}}', label: 'Email Address', category: 'Contact Info' },
  { value: '{{recipient_phone}}', label: 'Lead Phone', category: 'Contact Info' },
  // Agent Info
  { value: '{{phone}}', label: 'Your Phone (area code matched)', category: 'Agent Info' },
  { value: '{{my_phone}}', label: 'Your Phone', category: 'Agent Info' },
  { value: '{{agent_phone}}', label: 'Your Phone', category: 'Agent Info' },
  // Address
  { value: '{{address}}', label: 'Street Address', category: 'Address' },
  { value: '{{city}}', label: 'City', category: 'Address' },
  { value: '{{state}}', label: 'State', category: 'Address' },
  { value: '{{zip_code}}', label: 'ZIP Code', category: 'Address' },
  // Personal Details
  { value: '{{age}}', label: 'Age', category: 'Personal Details' },
  { value: '{{dob}}', label: 'Date of Birth', category: 'Personal Details' },
  { value: '{{gender}}', label: 'Gender', category: 'Personal Details' },
  { value: '{{marital_status}}', label: 'Marital Status', category: 'Personal Details' },
];

export const SentimentalsSettings = function() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: settingsData, isLoading: isLoadingSettings, error: settingsError } = useSentimentalSettings();
  const updateSettingsMutation = useUpdateSentimentalSettings();
  const testSendMutation = useTestSend();
  const { toast } = useToast();
  const { user } = useUserContext();
  const { data: userPhoneNumbers } = useUserPhoneNumbers();
  const isSuperAgent = user?.user?.accountType === "super_agent" || user?.user?.accountType === "enterprise";
  const { data: voiceClones = [] } = useVoiceClones();
  const hasReadyVoiceClone = isSuperAgent && voiceClones.some((c) => c.status === "ready");

  const [settings, setSettings] = useState<Partial<SentimentalSettings>>({
    enabled: false,
    automationMode: "manual",
    sendTime: "09:00",
    timezone: "America/New_York",
    scope: "personal",
    birthdayEnabled: false,
    policyAnniversaryEnabled: false,
    christmasEnabled: false,
    thanksgivingEnabled: false,
    newYearEnabled: false,
    lapsedPolicyEnabled: false,
    birthdayMessage: OCCASIONS[0].defaultMessage,
    policyAnniversaryMessage: OCCASIONS[1].defaultMessage,
    christmasMessage: OCCASIONS[2].defaultMessage,
    thanksgivingMessage: OCCASIONS[3].defaultMessage,
    newYearMessage: OCCASIONS[4].defaultMessage,
    lapsedPolicyMessage: OCCASIONS[5].defaultMessage,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<Partial<SentimentalSettings> | null>(null);

  // Feature states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewOccasion, setPreviewOccasion] = useState<OccasionType | null>(null);
  const [approvalQueueDialogOpen, setApprovalQueueDialogOpen] = useState(false);
  const [selectedOccasion, setSelectedOccasion] = useState<OccasionType>('birthday');

  // A2P verification state
  const [a2pStatus, setA2pStatus] = useState<{ canSend: boolean; reason?: string } | null>(null);
  const [checkingA2P, setCheckingA2P] = useState(true);

  // Refs for textareas to insert variables at cursor position
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  // Get initial sub-tab from URL or default to 'upcoming'
  const getInitialSubTab = (): 'settings' | 'messages' | 'upcoming' => {
    const tab = searchParams.get('subtab');
    if (tab === 'messages' || tab === 'upcoming' || tab === 'settings') {
      return tab;
    }
    return 'upcoming';
  };
  const [activeSubTab, setActiveSubTab] = useState<'settings' | 'messages' | 'upcoming'>(getInitialSubTab());

  // Update URL when sub-tab changes
  const handleSubTabChange = (newTab: 'settings' | 'messages' | 'upcoming') => {
    setActiveSubTab(newTab);
    const params = new URLSearchParams(searchParams.toString());
    params.set('subtab', newTab);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // Approval queue hooks
  const { data: schedulesData, isLoading: isLoadingSchedules } = useSentimentalSchedules(
    { status: 'pending_approval' },
    approvalQueueDialogOpen
  );
  const approveScheduleMutation = useApproveSchedule();
  const rejectScheduleMutation = useRejectSchedule();
  const approveAllMutation = useApproveAll();

  // Upcoming occasions hook (fetches real data from personal leads)
  const { data: upcomingOccasionsData, isLoading: isLoadingOccasions } = useUpcomingOccasions(60, settings.enabled);

  // Check A2P verification status on mount
  useEffect(() => {
    const checkA2PStatus = async () => {
      try {
        setCheckingA2P(true);
        const status = await a2pService.canSendSMS();
        setA2pStatus(status);
      } catch (error) {
        setA2pStatus({ canSend: false, reason: 'Failed to check A2P verification status' });
      } finally {
        setCheckingA2P(false);
      }
    };
    checkA2PStatus();
  }, []);

  // Track if we just saved to skip the next sync from settingsData
  const skipNextSyncRef = useRef(false);

  // Update local state when data is loaded
  useEffect(() => {
    // Skip this sync if we just saved (to prevent loop)
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    if (settingsData) {
      // Merge with defaults for messages
      const mergedSettings = {
        ...settingsData,
        birthdayMessage: settingsData.birthdayMessage || OCCASIONS[0].defaultMessage,
        policyAnniversaryMessage: settingsData.policyAnniversaryMessage || OCCASIONS[1].defaultMessage,
        christmasMessage: settingsData.christmasMessage || OCCASIONS[2].defaultMessage,
        thanksgivingMessage: settingsData.thanksgivingMessage || OCCASIONS[3].defaultMessage,
        newYearMessage: settingsData.newYearMessage || OCCASIONS[4].defaultMessage,
        lapsedPolicyMessage: settingsData.lapsedPolicyMessage || OCCASIONS[5].defaultMessage,
      };

      // Only update state if values actually changed to prevent infinite loops
      setSettings(prev => {
        const hasChanges = JSON.stringify(prev) !== JSON.stringify(mergedSettings);
        if (!hasChanges) return prev;
        return mergedSettings;
      });

      setOriginalSettings(prev => {
        const hasChanges = JSON.stringify(prev) !== JSON.stringify(mergedSettings);
        if (!hasChanges) return prev;
        return mergedSettings;
      });
    }
  }, [settingsData]);

  // Check for changes and auto-save
  useEffect(() => {
    if (originalSettings && !isSaving) {
      const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(hasChanges);

      if (hasChanges) {
        const timer = setTimeout(() => {
          handleSaveSettings();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [settings, originalSettings, isSaving]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Skip the next sync from settingsData to prevent loop
      skipNextSyncRef.current = true;
      await updateSettingsMutation.mutateAsync(settings);
      setOriginalSettings(settings);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save sentimental settings:', error);
      // Reset the skip flag on error so we can sync again
      skipNextSyncRef.current = false;
    } finally {
      setIsSaving(false);
    }
  };

  // Get the user's first purchased phone number for preview
  const getUserPhone = (): string => {
    if (userPhoneNumbers && userPhoneNumbers.length > 0) {
      const phone = userPhoneNumbers[0];
      // Phone number might be in 'number' or 'phoneNumber' property
      const phoneNum = phone.number || phone.phoneNumber || '';
      if (phoneNum) {
        // Format as (XXX) XXX-XXXX if it's a raw number
        const cleaned = phoneNum.replace(/\D/g, '');
        if (cleaned.length === 10) {
          return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
          return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        }
        return phoneNum; // Return as-is if already formatted
      }
    }
    return "(Your Phone Number)";
  };

  const previewMessage = (content: string): string => {
    if (!content) return "No message configured...";
    const userPhone = getUserPhone();
    return content
      // Basic Info
      .replace(/\{\{name\}\}/g, "John Doe")
      .replace(/\{\{first_name\}\}/g, "John")
      .replace(/\{\{last_name\}\}/g, "Doe")
      // Contact Info
      .replace(/\{\{email\}\}/g, "john.doe@email.com")
      .replace(/\{\{recipient_phone\}\}/g, "(555) 123-4567")
      // Agent Info - Use user's actual purchased phone number
      .replace(/\{\{phone\}\}/g, userPhone)
      .replace(/\{\{my_phone\}\}/g, userPhone)
      .replace(/\{\{agent_phone\}\}/g, userPhone)
      .replace(/\s*[-–—,|]?\s*\{\{agency_name\}\}/gi, '')
      // Address
      .replace(/\{\{address\}\}/g, "123 Main Street")
      .replace(/\{\{city\}\}/g, "Tampa")
      .replace(/\{\{state\}\}/g, "FL")
      .replace(/\{\{zip_code\}\}/g, "33601")
      // Personal Details
      .replace(/\{\{age\}\}/g, "45")
      .replace(/\{\{dob\}\}/g, "01/15/1979")
      .replace(/\{\{gender\}\}/g, "Male")
      .replace(/\{\{marital_status\}\}/g, "Married");
  };

  const calculateSmsMetrics = (content: string) => {
    const charCount = content.length;
    const segmentCount = Math.ceil(charCount / 160) || 1;
    const costPerSegment = 0.0079;
    return {
      charCount,
      segmentCount,
      costPerMessage: (segmentCount * costPerSegment).toFixed(4),
    };
  };

  const getBestSendTime = () => ({
    time: "09:00",
    reason: "Messages sent at 9 AM have the highest open rates.",
    confidence: 85,
  });

  const handleShowPreview = (occasion: OccasionType) => {
    setPreviewOccasion(occasion);
    setPreviewDialogOpen(true);
  };

  const handleShowApprovalQueue = () => {
    setApprovalQueueDialogOpen(true);
  };

  const handleApproveSchedule = async (scheduleId: string) => {
    try {
      await approveScheduleMutation.mutateAsync(scheduleId);
      toast({
        title: "Schedule Approved",
        description: "The message has been approved and will be sent as scheduled.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve the schedule. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRejectSchedule = async (scheduleId: string) => {
    try {
      await rejectScheduleMutation.mutateAsync(scheduleId);
      toast({
        title: "Schedule Rejected",
        description: "The message has been rejected and will not be sent.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject the schedule. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleApproveAll = async () => {
    try {
      // Format today's date in local timezone to avoid UTC shift
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      await approveAllMutation.mutateAsync(today);
      toast({
        title: "All Schedules Approved",
        description: "All pending messages have been approved.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve all schedules. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getMessageForOccasion = (occasionId: OccasionType): string => {
    const occasion = OCCASIONS.find(o => o.id === occasionId);
    if (!occasion) return '';
    return (settings as any)[occasion.messageKey] || occasion.defaultMessage;
  };

  const setMessageForOccasion = (occasionId: OccasionType, message: string) => {
    const occasion = OCCASIONS.find(o => o.id === occasionId);
    if (!occasion) return;
    setSettings(prev => ({ ...prev, [occasion.messageKey]: message }));
  };

  const resetMessageToDefault = (occasionId: OccasionType) => {
    const occasion = OCCASIONS.find(o => o.id === occasionId);
    if (!occasion) return;
    setSettings(prev => ({ ...prev, [occasion.messageKey]: occasion.defaultMessage }));
  };

  const insertVariable = (occasionId: OccasionType, variable: string) => {
    const textarea = textareaRefs.current[occasionId];
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentMessage = getMessageForOccasion(occasionId);
    const newMessage = currentMessage.substring(0, start) + variable + currentMessage.substring(end);
    setMessageForOccasion(occasionId, newMessage);

    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Filter out past schedules - only show today and future
  const pendingSchedules = useMemo(() => {
    if (!schedulesData) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return schedulesData.filter((schedule: any) => {
      // Parse the scheduled date
      const dateStr = schedule.scheduledDate;
      let scheduleDate: Date;

      if (dateStr.includes('T')) {
        // ISO format - extract date portion
        const utcDate = new Date(dateStr);
        scheduleDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
      } else {
        // YYYY-MM-DD format
        const [year, month, day] = dateStr.split('-').map(Number);
        scheduleDate = new Date(year, month - 1, day);
      }

      // Only include if schedule date is today or in the future
      return scheduleDate >= today;
    });
  }, [schedulesData]);

  const upcomingOccasions = upcomingOccasionsData || [];

  if (isLoadingSettings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Sentimentals Settings
          </CardTitle>
          <CardDescription>Loading settings...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (settingsError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Sentimentals Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load sentimental settings. Please try again.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const currentOccasion = OCCASIONS.find(o => o.id === selectedOccasion)!;

  return (
    <div className="space-y-6">
      {/* Main Sentimentals Card with Sub-tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Sentimentals
          </CardTitle>
          <CardDescription>
            Automatically send personalized birthday and holiday messages to your personal leads
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* A2P Verification Warning */}
          {!checkingA2P && !a2pStatus?.canSend && (
            <A2PGateNotice
              stage={(a2pStatus as any)?.stage || 'required'}
              reason={a2pStatus?.reason}
              onAction={() => router.push('/settings?tab=a2p-verification')}
            />
          )}

          {/* Sub-tabs Navigation */}
          <Tabs value={activeSubTab} onValueChange={(v) => handleSubTabChange(v as 'settings' | 'messages' | 'upcoming')}>
            <TabsList className="inline-flex w-full">
              <TabsTrigger value="upcoming" className="flex-1 flex items-center gap-2" disabled={!settings.enabled}>
                <Calendar className="h-4 w-4" />
                Upcoming
              </TabsTrigger>
              <TabsTrigger value="messages" className="flex-1 flex items-center gap-2" disabled={!settings.enabled}>
                <MessageSquare className="h-4 w-4" />
                Messages
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex-1 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </TabsTrigger>
            </TabsList>

            {/* Settings Tab Content */}
            <TabsContent value="settings" className="space-y-6 mt-4">
              {/* Master Enable Toggle */}
              <div className="flex items-center justify-between pb-4 border-b">
                <div className="space-y-0.5">
                  <Label htmlFor="enabled" className="text-base font-semibold">
                    Enable Sentimentals
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Turn on automated birthday and holiday messaging for your leads
                  </p>
                </div>
                <Switch
                  id="enabled"
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              {settings.enabled && (
                <>
                  {/* Automation Mode */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Automation Mode</Label>
                    <RadioGroup
                      value={settings.automationMode}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, automationMode: value as AutomationMode }))}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="manual" id="manual" />
                        <Label htmlFor="manual" className="font-normal cursor-pointer">
                          Manual Approval - Review and approve each message before sending
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="auto" id="auto" />
                        <Label htmlFor="auto" className="font-normal cursor-pointer">
                          Auto-Send - Automatically send messages without approval
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Send Time & Timezone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sendTime">
                        <Clock className="inline h-4 w-4 mr-1" />
                        Send Time
                      </Label>
                      <Input
                        id="sendTime"
                        type="time"
                        value={settings.sendTime}
                        onChange={(e) => setSettings(prev => ({ ...prev, sendTime: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Recommended: {getBestSendTime().time} ({getBestSendTime().reason})
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select
                        value={settings.timezone}
                        onValueChange={(value) => setSettings(prev => ({ ...prev, timezone: value }))}
                      >
                        <SelectTrigger id="timezone">
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((tz) => (
                            <SelectItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Messages Tab Content */}
            <TabsContent value="messages" className="mt-4">
              <Tabs value={selectedOccasion} onValueChange={(v) => setSelectedOccasion(v as OccasionType)}>
                <TabsList className="flex w-full overflow-x-auto gap-1 md:grid md:grid-cols-6 scrollbar-hide">
                  {OCCASIONS.map((occasion) => {
                    const Icon = occasion.icon;
                    const isEnabled = (settings as any)[occasion.enabledKey];
                    return (
                      <TabsTrigger
                        key={occasion.id}
                        value={occasion.id}
                        className="relative"
                      >
                        <Icon className="h-4 w-4 mr-1" />
                        {occasion.label}
                        {isEnabled && (
                          <span className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full" />
                        )}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {OCCASIONS.map((occasion) => (
                  <TabsContent key={occasion.id} value={occasion.id} className="space-y-4 mt-4">
                    {/* Enable toggle for this occasion */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                      <div className="space-y-0.5">
                        <Label className="text-base font-semibold">
                          Enable {occasion.label} Messages
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {occasion.description}
                        </p>
                      </div>
                      <Switch
                        checked={(settings as any)[occasion.enabledKey]}
                        onCheckedChange={(checked) =>
                          setSettings(prev => ({ ...prev, [occasion.enabledKey]: checked }))
                        }
                      />
                    </div>

                    {(settings as any)[occasion.enabledKey] && (
                      <>
                        {/* Delivery Channel — automatic based on voice clone status */}
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="space-y-0.5">
                            <Label className="text-sm font-medium">Delivery</Label>
                            <p className="text-xs text-muted-foreground">
                              {hasReadyVoiceClone
                                ? "SMS + AI voicemail drop using your cloned voice"
                                : isSuperAgent
                                  ? "SMS only — set up voice clone to add voicemail"
                                  : "SMS only"}
                            </p>
                          </div>
                          {hasReadyVoiceClone ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              SMS + Voicemail
                            </Badge>
                          ) : isSuperAgent ? (
                            <a
                              href="/settings?tab=account&accountTab=preferences&prefTab=dialer"
                              className="text-xs text-muted-foreground hover:text-foreground underline"
                            >
                              <Mic className="h-3 w-3 inline mr-1" />
                              Set up Voice Clone
                            </a>
                          ) : (
                            <a href="/settings?tab=subscription">
                              <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 cursor-pointer hover:bg-violet-100">
                                <Sparkles className="h-3 w-3 mr-1" />
                                Upgrade for Voicemail
                              </Badge>
                            </a>
                          )}
                        </div>

                        {/* Message Template */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Message Template</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => resetMessageToDefault(occasion.id)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Reset to Default
                            </Button>
                          </div>
                          <Select
                            onValueChange={(value) => insertVariable(occasion.id, value)}
                          >
                            <SelectTrigger id="insert-variable">
                              <SelectValue placeholder="Insert variable..." />
                            </SelectTrigger>
                            <SelectContent className="max-h-80">
                              {/* Group variables by category */}
                              {['Basic Info', 'Contact Info', 'Agent Info', 'Address', 'Personal Details'].map((category) => (
                                <SelectGroup key={category}>
                                  <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{category}</SelectLabel>
                                  {AVAILABLE_VARIABLES
                                    .filter((v) => v.category === category)
                                    .map((variable) => (
                                      <SelectItem key={variable.value} value={variable.value}>
                                        <span className="flex items-center gap-2">
                                          <span>{variable.label}</span>
                                          <span className="text-xs text-muted-foreground">{variable.value}</span>
                                        </span>
                                      </SelectItem>
                                    ))}
                                </SelectGroup>
                              ))}
                            </SelectContent>
                          </Select>
                          <Textarea
                            ref={(el) => { textareaRefs.current[occasion.id] = el; }}
                            value={getMessageForOccasion(occasion.id)}
                            onChange={(e) => setMessageForOccasion(occasion.id, e.target.value)}
                            placeholder="Enter your message..."
                            className="min-h-[120px]"
                          />
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>
                              Variables: {'{{first_name}}'}, {'{{last_name}}'}, {'{{name}}'}, {'{{phone}}'}
                            </span>
                            {(() => {
                              const metrics = calculateSmsMetrics(getMessageForOccasion(occasion.id));
                              return (
                                <span>
                                  {metrics.charCount} chars | {metrics.segmentCount} segment{metrics.segmentCount !== 1 ? 's' : ''}
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Preview */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Preview</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleShowPreview(occasion.id)}
                            >
                              <Smartphone className="h-4 w-4 mr-1" />
                              Mobile Preview
                            </Button>
                          </div>
                          <div className="p-3 border rounded-md bg-white min-h-[80px]">
                            <p className="text-sm whitespace-pre-wrap">
                              {previewMessage(getMessageForOccasion(occasion.id))}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </TabsContent>

            {/* Upcoming Occasions Tab Content */}
            <TabsContent value="upcoming" className="mt-4">
              {isLoadingOccasions ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : upcomingOccasions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Upcoming Occasions</h3>
                  <p className="text-muted-foreground text-sm">
                    No birthdays or policy anniversaries found for the next 60 days.
                    <br />
                    Make sure your leads have date of birth information saved.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  {upcomingOccasions.map((item) => {
                    const isClickable = item.leadId && item.leadId.length > 0;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                          isClickable ? 'cursor-pointer hover:bg-gray-50 hover:border-primary/30' : ''
                        }`}
                        onClick={() => {
                          if (isClickable) {
                            router.push(`/leads/${item.leadId}`);
                          }
                        }}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            router.push(`/leads/${item.leadId}`);
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            item.occasion === 'Birthday' ? 'bg-pink-100' :
                            item.occasion === 'Policy Anniversary' ? 'bg-purple-100' :
                            item.occasion === 'Christmas' ? 'bg-red-100' :
                            item.occasion === 'Thanksgiving' ? 'bg-orange-100' :
                            'bg-blue-100'
                          }`}>
                            {item.occasion === 'Birthday' ? <Cake className="h-5 w-5 text-pink-600" /> :
                             item.occasion === 'Policy Anniversary' ? <Heart className="h-5 w-5 text-purple-600" /> :
                             item.occasion === 'Christmas' ? <Gift className="h-5 w-5 text-red-600" /> :
                             item.occasion === 'Thanksgiving' ? <Heart className="h-5 w-5 text-orange-600" /> :
                             <PartyPopper className="h-5 w-5 text-blue-600" />}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{item.leadName}</p>
                            <p className="text-xs text-muted-foreground">{item.occasion}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className="font-medium text-sm">{item.date}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.daysAway === 0 ? 'Today' :
                               item.daysAway === 1 ? 'Tomorrow' :
                               `In ${item.daysAway} days`}
                            </p>
                          </div>
                          {isClickable && (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Save Status & Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isSaving && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </span>
          )}
          {!isSaving && !hasChanges && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <Check className="h-4 w-4" />
              All changes saved
            </span>
          )}
        </div>
        <Button onClick={handleShowApprovalQueue} variant="outline">
          <Send className="h-4 w-4 mr-2" />
          View Approval Queue
          {pendingSchedules.length > 0 && (
            <Badge variant="destructive" className="ml-2">
              {pendingSchedules.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Mobile Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Mobile Preview</DialogTitle>
            <DialogDescription>
              See how your message will appear on a mobile device
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="mx-auto w-full flex justify-center">
              <div className="relative border-gray-800 bg-gray-800 border-[10px] md:border-[14px] rounded-[2rem] md:rounded-[2.5rem] h-[400px] md:h-[500px] w-[220px] md:w-[280px] shadow-xl">
                <div className="w-[90px] md:w-[120px] h-[12px] md:h-[16px] bg-gray-800 top-0 rounded-b-[0.75rem] md:rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute"></div>
                <div className="rounded-[1.25rem] md:rounded-[1.5rem] overflow-hidden w-full h-full bg-white">
                  <div className="h-full bg-gray-100 p-3 md:p-4 overflow-y-auto">
                    <div className="flex justify-start">
                      <div className="bg-white rounded-lg p-3 max-w-[85%] shadow-sm">
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {previewOccasion && previewMessage(getMessageForOccasion(previewOccasion))}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Just now</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {previewOccasion && (() => {
              const metrics = calculateSmsMetrics(getMessageForOccasion(previewOccasion));
              return (
                <div className="text-center text-xs text-muted-foreground">
                  {metrics.charCount} chars | {metrics.segmentCount} segment{metrics.segmentCount !== 1 ? 's' : ''}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Approval Queue Dialog */}
      <Dialog open={approvalQueueDialogOpen} onOpenChange={setApprovalQueueDialogOpen}>
        <ResponsiveDialogContent size="3xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Approval Queue
            </DialogTitle>
            <DialogDescription>
              Review and approve scheduled sentimental messages before they are sent
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {isLoadingSchedules ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : pendingSchedules.length === 0 ? (
              <div className="text-center py-8">
                <Check className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
                <p className="text-muted-foreground">
                  No messages pending approval. New messages will appear here when scheduled.
                </p>
              </div>
            ) : (
              <>
                {/* A2P Warning in dialog */}
                {!checkingA2P && !a2pStatus?.canSend && (
                  <A2PGateNotice
                    stage={(a2pStatus as any)?.stage || 'required'}
                    reason={a2pStatus?.reason}
                    className="mb-4"
                  />
                )}

                <div className="flex items-center justify-between pb-4 border-b">
                  <span className="text-sm text-muted-foreground">
                    {pendingSchedules.length} message{pendingSchedules.length !== 1 ? 's' : ''} pending approval
                  </span>
                  <Button
                    size="sm"
                    onClick={handleApproveAll}
                    disabled={approveAllMutation.isPending || !a2pStatus?.canSend}
                    title={!a2pStatus?.canSend ? "Complete A2P verification to approve messages" : undefined}
                  >
                    {approveAllMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Approve All
                  </Button>
                </div>

                <div className="space-y-3">
                  {pendingSchedules.map((schedule: any) => (
                    <Card key={schedule.id} className="overflow-hidden">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">
                                {schedule.leadName || `${schedule.lead?.firstName || ''} ${schedule.lead?.lastName || ''}`.trim() || 'Unknown'}
                              </span>
                              <Badge variant="outline" className="capitalize">
                                {schedule.occasionType?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5" />
                                <span>
                                  Scheduled: {(() => {
                                    // Handle both YYYY-MM-DD and ISO formats without timezone shift
                                    const dateStr = schedule.scheduledDate;
                                    let year: number, month: number, day: number;
                                    if (dateStr.includes('T')) {
                                      const utcDate = new Date(dateStr);
                                      year = utcDate.getUTCFullYear();
                                      month = utcDate.getUTCMonth();
                                      day = utcDate.getUTCDate();
                                    } else {
                                      [year, month, day] = dateStr.split('-').map(Number);
                                      month = month - 1;
                                    }
                                    const localDate = new Date(year, month, day, 12, 0, 0);
                                    return localDate.toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    });
                                  })()} at {schedule.scheduledTime}
                                </span>
                              </div>
                              {/* Client's local time */}
                              {schedule.leadState && (() => {
                                const clientTime = getClientLocalTime(
                                  schedule.scheduledDate,
                                  schedule.scheduledTime,
                                  schedule.leadState
                                );
                                return clientTime ? (
                                  <p className="text-blue-600">
                                    Client&apos;s local time: {clientTime.localTime} {clientTime.abbrev}
                                    <span className="text-muted-foreground"> ({schedule.leadState})</span>
                                  </p>
                                ) : null;
                              })()}
                            </div>
                            <div className="p-3 bg-gray-50 rounded-md mt-2">
                              <p className="text-sm whitespace-pre-wrap">{schedule.message}</p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleApproveSchedule(schedule.id)}
                              disabled={approveScheduleMutation.isPending || !a2pStatus?.canSend}
                              title={!a2pStatus?.canSend ? "Complete A2P verification to approve messages" : undefined}
                            >
                              {approveScheduleMutation.isPending ? (
                                <Loader2 className="h-4 w-4" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRejectSchedule(schedule.id)}
                              disabled={rejectScheduleMutation.isPending}
                            >
                              {rejectScheduleMutation.isPending ? (
                                <Loader2 className="h-4 w-4" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalQueueDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
};
